import { buildImgixSrc, cleanImgixSrc } from 'shared/components/imgix';
import type { ImgixParams } from 'shared/components/imgix';
import type { ImgixSizing } from './components/image-style';

// The two production S3 buckets `replaceAWSSourceWithImgix` (called by `cleanImgixSrc`) rewrites
// to Imgix -- duplicated here (rather than reaching into `packages/shared/helpers/imgix.ts`'s
// private, unexported LEGACY_AWS_SOURCE/NEWER_AWS_SOURCE constants) so this module can gate *when*
// that rewrite runs. Keep these in sync if that module's source URLs ever change.
const AWS_S3_IMAGE_SOURCES = [
  'https://s3-us-west-2.amazonaws.com/dutchie-images/',
  'https://dutchie-images.s3.us-west-2.amazonaws.com/',
];

/**
 * True only for the exact S3 URL shapes `cleanImgixSrc`/`replaceAWSSourceWithImgix` know how to
 * rewrite (both the `https://` and non-TLS `http://` variants of both buckets).
 *
 * This is a PREFIX match, not `includes()`. Prefix-matching prevents an attacker-controlled URL
 * that merely contains the S3 host as a substring (e.g. in a query param) from being
 * misclassified as a trusted S3 asset.
 */
export function isAwsS3ImageSrc(src: string): boolean {
  // Trim before prefix-checking so a whitespace-padded but otherwise genuine S3 URL still matches.
  const trimmed = src.trim();
  return AWS_S3_IMAGE_SOURCES.some(
    (source) => trimmed.startsWith(source) || trimmed.startsWith(source.replace('https://', 'http://'))
  );
}

/**
 * The single gate every CMS image pick-time and editor-display-time call site must use before
 * calling `cleanImgixSrc` (`asset-picker-field.tsx`'s `handleSelect`, its preview thumbnail, and
 * `asset-picker-modal.tsx`'s `renderThumbnail`).
 *
 * Not used by the production `image.tsx` renderer: S3 rewriting happens at pick time only, so
 * only newly chosen assets get resolved to Imgix. Old S3-hosted content renders byte-identical.
 *
 * `cleanImgixSrc` is unsafe to call unconditionally: any `src` without `'://'` (relative paths,
 * `data:` URIs, incomplete URLs) is silently replaced by a hardcoded stock photo fallback.
 * Gating to S3 shapes only keeps every other src resolving to exactly what was passed in.
 */
export function resolveImageSrc(src: string | undefined): string {
  if (!src) return '';
  return isAwsS3ImageSrc(src) ? cleanImgixSrc(src) : src;
}

// Not exported by `packages/shared/helpers/imgix.ts` -- duplicated here for the same reason
// AWS_S3_IMAGE_SOURCES is above (see that constant's comment): this module needs to gate *when*
// the shared Imgix URL-building helpers run, without reaching into that module's private
// constants. Keep in sync if the shared Imgix domain ever changes.
const IMGIX_HOSTED_SOURCES = ['https://images.dutchie.com/', 'http://images.dutchie.com/'];

/**
 * True for a src already hosted on Dutchie's Imgix domain -- emitted by newly-picked
 * asset-library images and any S3 URL after `cleanImgixSrc` rewrites it at pick time.
 * Prefix-matched for the same reason as {@link isAwsS3ImageSrc}.
 */
export function isImgixHostedSrc(src: string): boolean {
  return IMGIX_HOSTED_SOURCES.some((source) => src.startsWith(source));
}

/** Delivered `<img>` attributes for an Imgix-hosted source: an optimised default `src`, a
 * `srcSet`, and an optional `sizes` hint. Fixed-width images use `x` descriptors (no `sizes`);
 * fluid images use `w` descriptors with `sizes: '100vw'`. */
export type ImgixDelivery = {
  src: string;
  srcSet: string;
  sizes?: string;
};

/**
 * Percent-encodes characters that are unambiguous in an HTTP URL path but would corrupt a
 * comma-delimited or whitespace-tokenised `srcSet` string if left literal:
 *
 * - `,` → `%2C`: srcSet is comma-delimited, so a raw comma inside a candidate URL splits the list
 *   at the wrong point, giving the browser extra phantom entries and breaking all responsive logic.
 * - ` ` → `%20`: srcSet candidates are whitespace-tokenised to separate the URL from its `w`/`x`
 *   descriptor; a raw space inside the URL causes the browser to truncate the URL at the space,
 *   making the request 404.
 *
 * Applied only to URLs built for the `srcSet` string; the scalar `src` attribute does not have
 * this ambiguity (there is no delimiter to confuse).
 *
 * The encoding targets path-level characters only — the query string's `auto=format,compress` is
 * already encoded by `URLSearchParams` / the Imgix URL-builder, so this step is a no-op for those
 * characters in the query string (they arrive as `%2C` from the builder already).
 */
function encodeSrcSetUrl(url: string): string {
  // Encode commas and spaces that survive path-level string concatenation unencoded.
  return url.replace(/%20| /g, '%20').replace(/%2C|,/gi, '%2C');
}

/**
 * Builds the delivered `src`/`srcSet`/`sizes` for an Imgix-hosted image.
 *
 * `auto: 'format,compress'` picks the smallest acceptable format per-browser (e.g. WebP/AVIF)
 * and applies Imgix's automatic compression; `q: 70` is the quality budget used across marketplace.
 *
 * Fixed widths (`kind: 'fixed'`) use DPR `x` descriptors (`1x/2x/3x`) — the browser ignores the
 * `sizes` attribute for `x`-descriptor srcSets, so emitting it would be dead weight. The
 * delivered width IS the CSS intrinsic width, keeping `fit-content` layout stable.
 *
 * Fluid widths (`kind: 'fluid'`) use `w` descriptors + `sizes: '100vw'` so the browser picks the
 * candidate that actually matches its viewport width. `100vw` is a deliberate upper bound — the
 * component can't see its Section's `maxWidth`, so it may overfetch slightly on constrained
 * sections, which is the safe direction (never soft).
 */
export function buildImgixDelivery(src: string, sizing: ImgixSizing): ImgixDelivery {
  if (sizing.kind === 'fixed') {
    const params: ImgixParams = { auto: 'format,compress', q: 70, w: sizing.widthPx };
    // Build each DPR candidate locally — mirroring what the fluid path does — so no pre-joined
    // string is ever split. Splitting a pre-built srcSet string on ', ' is the flaw the fluid path
    // avoids: a literal ", " inside a URL path (reachable by pasting e.g.
    // "https://images.dutchie.com/summer, 2024/banner.jpg" into the free-text field) would break a
    // candidate mid-URL before encoding runs. Building and encoding each candidate individually
    // prevents this: `encodeSrcSetUrl` runs on the raw URL before it ever joins the comma-delimited
    // list, so there is no ", " to split on in the first place.
    const srcSet = [1, 2, 3]
      .map((dpr) => `${encodeSrcSetUrl(buildImgixSrc(src, { ...params, dpr }))} ${dpr}x`)
      .join(', ');
    return {
      src: buildImgixSrc(src, params),
      srcSet,
    };
  }
  // Fluid: build a w-descriptor srcSet locally (not using the shared x-descriptor helper).
  // Encode commas and spaces in each built URL before joining into the comma-delimited srcSet
  // string — a literal comma in an Imgix URL path (reachable via the free-text paste route in
  // the asset picker) would corrupt the list and break all responsive image selection.
  const srcSet = sizing.candidateWidthsPx
    .map((w) => `${encodeSrcSetUrl(buildImgixSrc(src, { auto: 'format,compress', q: 70, w }))} ${w}w`)
    .join(', ');
  return {
    src: buildImgixSrc(src, { auto: 'format,compress', q: 70, w: 1600 }),
    srcSet,
    sizes: '100vw',
  };
}
